import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { InteractionBlueprintData } from "@/lib/contentParsers/interactionBlueprintSchema";
import {
  sparxsEditorCatalogReplaced,
  sparxsEditorSaveSucceededForFiles,
} from "@/features/sparxsEditor/store/domainActions";

export type SparxsBlueprintCacheEntry = {
  blueprint: InteractionBlueprintData;

  lastKnownServerModified?: number;

  listLastModified?: number;
};

export type SparxsBlueprintCacheState = {
  byFileName: Record<string, SparxsBlueprintCacheEntry>;
};

const initialState: SparxsBlueprintCacheState = {
  byFileName: {},
};

const sparxsBlueprintCacheSlice = createSlice({
  name: "sparxsBlueprintCache",
  initialState,
  reducers: {
    upsertSparxBlueprintCacheEntry: (
      state,
      action: PayloadAction<{
        fileName: string;
        blueprint: InteractionBlueprintData;
        lastKnownServerModified?: number;
        listLastModified?: number;
      }>,
    ) => {
      const { fileName, blueprint, lastKnownServerModified, listLastModified } =
        action.payload;
      state.byFileName[fileName] = {
        blueprint,
        ...(lastKnownServerModified != null && { lastKnownServerModified }),
        ...(listLastModified != null && { listLastModified }),
      };
    },
    removeSparxBlueprintCacheEntry: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      const { [key]: _removed, ...rest } = state.byFileName;
      state.byFileName = rest;
    },
    pruneSparxsBlueprintCacheToFileNames: (
      state,
      action: PayloadAction<{ keepFileNames: string[] }>,
    ) => {
      const keep = new Set(action.payload.keepFileNames);
      const next: Record<string, SparxsBlueprintCacheEntry> = {};
      for (const name of keep) {
        const entry = state.byFileName[name];
        if (entry != null) {
          next[name] = entry;
        }
      }
      state.byFileName = next;
    },
    clearSparxBlueprintCache: (state) => {
      state.byFileName = {};
    },
  },

  extraReducers: (builder) => {
    builder.addCase(sparxsEditorCatalogReplaced, (state, action) => {
      const keep = new Set(action.payload.files.map((f) => f.fileName));
      const next: Record<string, SparxsBlueprintCacheEntry> = {};
      for (const name of keep) {
        const entry = state.byFileName[name];
        if (entry != null) {
          next[name] = entry;
        }
      }
      state.byFileName = next;
    });
    builder.addCase(sparxsEditorSaveSucceededForFiles, (state, action) => {
      for (const row of action.payload.saved) {
        const lm = row.serverLastModifiedMs;
        state.byFileName[row.fileName] = {
          blueprint: row.blueprint,
          lastKnownServerModified: lm,
          listLastModified: lm,
        };
      }
    });
  },
});

export const {
  upsertSparxBlueprintCacheEntry,
  removeSparxBlueprintCacheEntry,
  pruneSparxsBlueprintCacheToFileNames,
  clearSparxBlueprintCache,
} = sparxsBlueprintCacheSlice.actions;

export default sparxsBlueprintCacheSlice.reducer;
